import client from './index'
import { User } from '@/models/user'

export interface EvCarProfile {
  place_id?: string
  model_id?: number
  model_make_id?: string
  model_name: string
  model_year: number
  model_trim?: string
  battery_capacity: number
  charging_connector_type: string
  // | 'Type 1 (J1772)'
  // | 'Type 2 (Mennekes)'
  // | 'CHAdeMO'
  // | 'CCS (Combined Charging System)'
  // | 'Tesla Supercharger';
  charging_time: number
  range_per_full_charge: number
  price_per_minute_ev: number
  price_per_km_ev: number
  price_per_minute_up_to_100_km_ev: number
  price_per_minute_above_100_km_ev: number
  price_per_hour_above_100_km_ev: number
  price_per_hour_up_to_100_km_ev: number
  pause_price_ev: number
  deposit_amount_ev: number
  car_type: string
  address?: string
  transmission: 'automatic'
  fuel_type: 'electric'
  no_branded_title: boolean
  identity_country?: string
  plate_number: string
  car_description: string
  charging_included?: boolean
  battery_included?: boolean
  charging_policy?: string
  status?: string
  user_id: string
  gps?: boolean
  air_conditioner?: boolean
  seats?: number
  doors?: number
  bluetooth_stereo?: boolean
  wifi_internet?: boolean
  fire_extinguisher?: boolean
  ownership_permit?: string[]

  // Additional fields from validators
  electric_level?: number
  remaining_distance?: number
  start_price?: number
  max_distance_per_tariff?: number
  reservation_timer?: number
  reservation_extension_price?: number
  reservation_status?: string
  pause_limit?: number
  pause_usage_limit?: boolean
  engine_status?: boolean
  no_smoking?: boolean
  current_location?: string
  registered_car?: boolean
  external_id?: string
  registration_status?: string
  battery_details?: string
  supported_charging_types?: string[]
  type_connection_geotek?: string
  location: {
    lat: number
    lng: number
  }
  min_age_requirement?: number
  activate_key_ev_car?: boolean
  copy_from_id?: string
  images?: any[]
  owner_ev_car?: string | null
}

export interface ApiResponse<T> {
  message: string
  status: string
  code: number
  data: T
  meta?: {
    total: number
    perPage: number
    currentPage: number
    lastPage: number
    firstPage: string
    firstPageUrl: string
    lastPageUrl: string
    nextPageUrl: string
    previousPageUrl: string
  }
  id?: string
}

export interface ApiError {
  message: string
  status: string
  code: number
  errors?: Record<string, unknown>
}

export interface UploadedImage {
  file?: File
  preview: string
}

export interface DamageReport {
  id: string
  description: string
  status: string
  user_id: string
  ev_car_profile_id: string
  images: Array<{ public_id: string; secure_url: string }>
  created_at: string
  updated_at: string
  user?: User
  vehicle?: EvCarProfile
}

export interface GroupedDamageReports {
  active: DamageReport[]
  resolved: DamageReport[]
  active_count: number
}

export interface EvReportsMeta {
  total: number
  active: {
    total: number
    perPage: number
    currentPage: number
    lastPage: number
  }
  resolved: {
    total: number
    perPage: number
    currentPage: number
    lastPage: number
  }
}

export interface EvReportsResponse {
  data: GroupedDamageReports
  meta: EvReportsMeta
}

export type IncludeReportRelation = 'user' | 'vehicle'

export interface CarControlResponse {
  message: string
  timesync: string
  data: {
    status: string
    result: string
  }
}

export const createEvCarProfile = async (data: Omit<EvCarProfile, 'id'>) => {
  return client.post<ApiResponse<{ id: string }>>('/ev-cars', data)
}

export interface GeotekCarProfile {
  id: string
  name: string
  comment: string
  devicetype: string
}

export const getEvCarGeotekProfiles = async (params: Record<string, 'id'>) => {
  return client.get<GeotekCarProfile[]>('/ev-cars/geotek-all-cars', {
    params,
  })
}

export const getEvCarGeotekLocation = async (
  external_id: string
): Promise<{ lat: number; lng: number }> => {
  const response = await client.get<{ lat: number; lng: number }>(
    `/ev-cars/geotek-car-location/${external_id}`
  )

  return response.data
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getEvCarProfiles = async (params: Record<string, any>) => {
  return client.get<ApiResponse<EvCarProfile[]>>('/ev-cars', { params })
}

export const getEvCarProfileById = async (id: string) => {
  return client.get<ApiResponse<EvCarProfile>>(`/ev-cars/${id}`)
}

export const getEvCarDamageReports = async (carId: string) => {
  return client.get<ApiResponse<DamageReport[]>>(
    '/ev-cars/ev-car-damage-reports',
    { params: { ev_car_profile_id: carId } }
  )
}

export const markDamageReportResolved = async (reportId: string) => {
  return client.post<ApiResponse<DamageReport>>(
    `/ev-cars/ev-car-damage-reports/${reportId}`
  )
}

export const updateEvCarProfile = async (
  id: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: Record<string, any> | FormData
) => {
  let requestData: FormData

  if (!(data instanceof FormData)) {
    requestData = new FormData()
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== 'undefined') {
        if (typeof value === 'object') {
          requestData.append(key, JSON.stringify(value))
        } else {
          requestData.append(key, String(value))
        }
      }
    })
  } else {
    requestData = data
  }

  return client.put<ApiResponse<EvCarProfile>>(`/ev-cars/${id}`, requestData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

export const deleteEvCarProfile = async (id: string) => {
  return client.delete<ApiResponse<null>>(`/ev-cars/${id}`)
}

export const updateEvCarOwner = async (
  id: string,
  ownerEvCar: string | null
) => {
  return client.put<ApiResponse<EvCarProfile>>(`/ev-cars/${id}/owner`, {
    owner_ev_car: ownerEvCar,
  })
}
export const setEvCarAssignedUser = async (
  id: string,
  userId: string | null
) => {
  return client.put<ApiResponse<EvCarProfile>>(`/ev-cars/${id}/assigned-user`, {
    user_id: userId,
  })
}

export const deleteEvCarImage = (carId: string, imageId: string) => {
  return client.delete<ApiResponse<null>>(`/ev-cars/${carId}/images/${imageId}`)
}

export const uploadEvCarImage = async (
  carId: string,
  file: File,
  type: string
) => {
  const formData = new FormData()
  formData.append('image', file)
  formData.append('type', type)
  return client.post<ApiResponse<{ secure_url: string; public_id: string }>>(
    `/ev-cars/${carId}/images`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  )
}

export const getEvCarImages = async (carId: string) => {
  return client.get<ApiResponse<{ secure_url: string; public_id: string }[]>>(
    `/ev-cars/${carId}/images`
  )
}

export const createEvCarAvailability = async (
  carId: string,
  data: { intervals: Array<{ start_date: string; end_date: string }> }
) => {
  return client.post<ApiResponse<null>>(
    `/ev-cars/${carId}/availabilities`,
    data
  )
}

export const getEvCarAvailability = async (carId: string) => {
  return client.get<
    ApiResponse<
      Array<{
        id: string
        start_date: string
        end_date: string
      }>
    >
  >(`/ev-cars/${carId}/availabilities`)
}

export const deleteEvCarAvailability = async (
  carId: string,
  availabilityId: string
) => {
  return client.delete<ApiResponse<null>>(
    `/ev-cars/${carId}/availabilities/${availabilityId}`
  )
}

// export const controlEvCarDoors = async (
//   carId: string,
//   action: 'lock' | 'unlock'
// ) => {
//   // eslint-disable-next-line @typescript-eslint/no-explicit-any
//   return client.post<ApiResponse<any>>(
//     `/ev-cars-control/${carId}/door-control`,
//     {
//       action,
//     }
//   )
// }

// export const controlEvCarWindows = async (
//   carId: string,
//   action: 'lock' | 'unlock'
// ) => {
//   // eslint-disable-next-line @typescript-eslint/no-explicit-any
//   return client.post<ApiResponse<any>>(
//     `/ev-cars-control/${carId}/windows-control`,
//     {
//       action,
//     }
//   )
// }

// export const controlEvCarEngine = async (
//   carId: string,
//   action: 'lock' | 'unlock'
// ) => {
//   // eslint-disable-next-line @typescript-eslint/no-explicit-any
//   return client.post<ApiResponse<any>>(
//     `/ev-cars-control/${carId}/engine-control`,
//     {
//       action,
//     }
//   )
// }

export const activateEvCarKey = async (carId: string) => {
  return client.post<ApiResponse<{ status: string; result: string }>>(
    `/ev-cars-control/${carId}/activate-key`
  )
}

export const controlEvCarDoors = async (
  carId: string,
  action: 'lock' | 'unlock'
) => {
  return client.post<CarControlResponse>(
    `/ev-cars-control/${carId}/door-control`,
    { action }
  )
}

export const controlEvCarWindows = async (
  carId: string,
  action: 'lock' | 'unlock'
) => {
  return client.post<CarControlResponse>(
    `/ev-cars-control/${carId}/windows-control`,
    { action }
  )
}

export const controlEvCarEngine = async (
  carId: string,
  action: 'lock' | 'unlock'
) => {
  return client.post<CarControlResponse>(`/ev-cars/${carId}/control/engine`, {
    action,
  })
}

export const getEvVehicleTrips = async (
  vehicleId: string,
  params?: {
    page?: number
    perPage?: number
    sortBy?: string
    sortDirection?: 'asc' | 'desc'
  }
) => {
  return client.get<ApiResponse<any>>(`/ev-trips/car/${vehicleId}`, { params })
}

export interface EvMapCar {
  id: string
  model_make_id: string
  model_name: string
  status: string
  booking_status?: string
  location: {
    lat: number
    lng: number
  }
  external_id: string
  electric_level: number
  range_per_charge: number
  updated_at: string
  images: Array<{
    id: string
    ev_car_profile_id: string
    public_id: string
    secure_url: string
    type: string
    createdAt: string
    updatedAt: string
  }>
  favorite: boolean
}

export interface GetEvMapCarsParams {
  status?: string | string[]
  page?: number
  perPage?: number
}

export const getEvMapCars = async (params: GetEvMapCarsParams = {}) => {
  const queryParams = new URLSearchParams()

  if (params.status) {
    if (Array.isArray(params.status)) {
      params.status.forEach(status => queryParams.append('status', status))
    } else {
      queryParams.append('status', params.status)
    }
  }

  if (params.page) {
    queryParams.append('page', params.page.toString())
  }

  if (params.perPage) {
    queryParams.append('perPage', params.perPage.toString())
  }

  const url = `/ev-maps/cars?${queryParams.toString()}`

  const response = await client.get(url)

  return response.data
}

export const getEvMapCarById = async (id: string) => {
  const url = `/ev-maps/car/${id}`

  const response = await client.get(url)

  return response.data
}

export interface TripLocation {
  lat: number
  lng: number
}

export interface TripPoint {
  id: string
  start_location: TripLocation
  end_location: TripLocation
  start_date: string
  end_date: string
  status: string
}

export interface EvStatistics {
  totalVehicles?: number
  totalUsers?: number
  totalTrips?: number
  revenue?: number
  avgTripDurationMinutes?: number
  avgTripDistanceKm?: number
  tripPoints?: TripPoint[]
  uniqueUsers?: number
  ongoingTrips?: number
}

export interface EvStatisticsRangeMeta {
  range?: {
    date_from: string
    date_to: string
  }
}

export interface EvStatisticsResponse {
  data: EvStatistics
  meta?: EvStatisticsRangeMeta
}

export interface GetEvStatisticsParams {
  date_from?: string
  date_to?: string
}

// Trip points (paginated)
export interface RawTripPoint {
  id: string
  // Backend may return either "lat,lng" string or { lat, lng } object
  start_location: string | { lat: number; lng: number } | null
  end_location: string | { lat: number; lng: number } | null
  start_date: string
  end_date: string
  status: string
}

export interface TripPointsPagination {
  page: number
  per_page: number
  total: number
  last_page: number
}

export interface EvTripPointsResponse {
  data: RawTripPoint[]
  meta?: {
    pagination?: TripPointsPagination
    range?: EvStatisticsRangeMeta['range']
  }
}

export interface GetEvTripPointsParams extends GetEvStatisticsParams {
  page?: number
  per_page?: number
}

// Trip intervals
export type TripIntervalsGrouping = 'daily' | 'weekly' | 'monthly'

export interface TripIntervalBucket {
  date: string
  avg_interval_minutes: number
  intervals_count: number
}

export interface EvTripIntervals {
  avgIntervalMinutes: number
  medianIntervalMinutes: number
  minIntervalMinutes: number
  maxIntervalMinutes: number
  intervalsCount: number
  buckets: TripIntervalBucket[]
}

export interface EvTripIntervalsResponse {
  data: EvTripIntervals
  meta?: EvStatisticsRangeMeta & {
    cached?: boolean
    cache_ttl_seconds?: number
  }
}

export interface GetEvTripIntervalsParams extends GetEvStatisticsParams {
  grouping?: TripIntervalsGrouping
  ev_car_profile_id?: string
  user_id?: string
}

// Time-of-day
export type TimeOfDaySegmentKey = 'morning' | 'afternoon' | 'evening'

export interface TimeOfDaySegment {
  trips: number
  revenue: number
  avg_duration_minutes: number
  avg_distance_km: number
}

export interface TimeOfDayExcluded {
  trips: number
  hours_range: string
}

export interface EvTimeOfDay {
  segments: Record<TimeOfDaySegmentKey, TimeOfDaySegment>
  excluded?: TimeOfDayExcluded
  hourly: Array<{ hour: number; trips: number }>
}

export interface EvTimeOfDayResponse {
  data: EvTimeOfDay
  meta?: EvStatisticsRangeMeta & {
    timezone?: string
    cached?: boolean
    cache_ttl_seconds?: number
  }
}

// Unique Users dynamics
export interface EvUniqueUsersBucket {
  date: string
  unique_users: number
}

export interface EvUniqueUsersDynamics {
  buckets: EvUniqueUsersBucket[]
}

export interface EvUniqueUsersDynamicsResponse {
  data: EvUniqueUsersDynamics
  meta?: EvStatisticsRangeMeta & {
    grouping?: string
  }
}

// Helper: unwrap { data: { success, data, meta } } -> { data, meta }
const unwrap = <T>(response: any): { data: T; meta?: any } => {
  const outer = response?.data ?? {}
  const inner = outer?.data ?? outer
  return {
    data: (inner?.data ?? inner) as T,
    meta: inner?.meta ?? outer?.meta,
  }
}

export const getEvStatistics = async (
  params?: GetEvStatisticsParams
): Promise<EvStatisticsResponse> => {
  const response = await client.get('/ev-statistics', { params })
  return unwrap<EvStatistics>(response)
}

export const getEvTripPoints = async (
  params?: GetEvTripPointsParams
): Promise<EvTripPointsResponse> => {
  const response = await client.get('/ev-statistics/trip-points', { params })
  const outer = response?.data ?? {}
  const inner = outer?.data ?? outer
  return {
    data: (inner?.data ?? inner ?? []) as RawTripPoint[],
    meta: inner?.meta ?? outer?.meta,
  }
}

export const getEvTripIntervals = async (
  params?: GetEvTripIntervalsParams
): Promise<EvTripIntervalsResponse> => {
  const response = await client.get('/ev-statistics/trip-intervals', { params })
  return unwrap<EvTripIntervals>(response)
}

export const getEvTimeOfDay = async (
  params?: GetEvStatisticsParams
): Promise<EvTimeOfDayResponse> => {
  const response = await client.get('/ev-statistics/time-of-day', { params })
  return unwrap<EvTimeOfDay>(response)
}

export const getEvUniqueUsersDynamics = async (
  params?: GetEvStatisticsParams & { grouping?: string }
): Promise<EvUniqueUsersDynamicsResponse> => {
  const response = await client.get('/ev-statistics/unique-users', { params })
  return unwrap<EvUniqueUsersDynamics>(response)
}

// Owner statistics endpoints (for ev_car_owner role)
export const getOwnerStatisticsCars = async () => {
  return client.get<ApiResponse<EvCarProfile[]>>('/ev-owner-statistics/cars')
}

export const getOwnerStatistics = async (
  params?: GetEvStatisticsParams
): Promise<EvStatisticsResponse> => {
  const response = await client.get('/ev-owner-statistics', { params })
  return unwrap<EvStatistics>(response)
}

export const getOwnerStatisticsByCar = async (
  carId: string,
  params?: GetEvStatisticsParams
): Promise<EvStatisticsResponse> => {
  const response = await client.get(`/ev-owner-statistics/cars/${carId}`, {
    params,
  })
  return unwrap<EvStatistics>(response)
}

export const getOwnerTripPoints = async (
  params?: GetEvStatisticsParams & { page?: number; perPage?: number }
): Promise<EvTripPointsResponse> => {
  const response = await client.get('/ev-owner-statistics/trip-points', {
    params,
  })
  const outer = response?.data ?? {}
  const inner = outer?.data ?? outer
  return {
    data: (inner?.data ?? inner ?? []) as RawTripPoint[],
    meta: inner?.meta ?? outer?.meta,
  }
}

export const getOwnerTripIntervals = async (
  params?: GetEvStatisticsParams & {
    grouping?: string
    ev_car_profile_id?: string
  }
): Promise<EvTripIntervalsResponse> => {
  const response = await client.get('/ev-owner-statistics/trip-intervals', {
    params,
  })
  return unwrap<EvTripIntervals>(response)
}

export const getOwnerTimeOfDay = async (
  params?: GetEvStatisticsParams & { ev_car_profile_id?: string }
): Promise<EvTimeOfDayResponse> => {
  const response = await client.get('/ev-owner-statistics/time-of-day', {
    params,
  })
  return unwrap<EvTimeOfDay>(response)
}

export const getOwnerUniqueUsersDynamics = async (
  params?: GetEvStatisticsParams & {
    grouping?: string
    ev_car_profile_id?: string
  }
): Promise<EvUniqueUsersDynamicsResponse> => {
  const response = await client.get('/ev-owner-statistics/unique-users', {
    params,
  })
  return unwrap<EvUniqueUsersDynamics>(response)
}

export interface GetEvReportsParams {
  include?: IncludeReportRelation[]
  page?: number
  perPage?: number
  sortBy?: string
  sortDirection?: 'asc' | 'desc'
}

export const getEvReports = async (
  params: GetEvReportsParams = {}
): Promise<EvReportsResponse> => {
  const queryParams: Record<string, string> = {}

  if (params.include?.length) {
    queryParams.include = params.include.join(',')
  }

  if (params.page) {
    queryParams.page = params.page.toString()
  }

  if (params.perPage) {
    queryParams.perPage = params.perPage.toString()
  }

  if (params.sortBy) {
    queryParams.sortBy = params.sortBy
  }

  if (params.sortDirection) {
    queryParams.sortDirection = params.sortDirection
  }

  const response = await client.get<ApiResponse<EvReportsResponse>>(
    '/ev-reports',
    {
      params: queryParams,
    }
  )

  return response.data.data
}

export const changeStatusReport = async (reportId: string) => {
  return client.post<ApiResponse<DamageReport>>(`/ev-report/${reportId}`)
}

const evCarsApi = {
  createEvCarProfile,
  getEvCarGeotekProfiles,
  getEvCarGeotekLocation,
  getEvCarProfiles,
  updateEvCarProfile,
  getEvCarDamageReports,
  markDamageReportResolved,
  deleteEvCarProfile,
  updateEvCarOwner,
  setEvCarAssignedUser,
  deleteEvCarImage,
  uploadEvCarImage,
  getEvCarImages,
  getEvCarProfileById,
  createEvCarAvailability,
  getEvCarAvailability,
  deleteEvCarAvailability,
  activateEvCarKey,
  controlEvCarDoors,
  controlEvCarWindows,
  controlEvCarEngine,
  getEvVehicleTrips,
  getEvMapCars,
  getEvStatistics,
  getEvReports,
  changeStatusReport,
}

export default evCarsApi
